import React, { useState, useEffect, useCallback } from 'react';
import { BottomSheet, StatusActionButton, HorizontalCategoryTabs } from '../../../shared/ui';
import {
  sitePaymentAllocationV2Api,
  UnpaidSessionV2,
  UnpaidActivityV2,
  UnpaidContractV2,
  UnpaidEarningForAllocationV2,
  PaymentAllocationV2Request,
  getPaymentAllocationConflictMessage
} from '../../../api/sitePaymentAllocationV2Api';
import './PaymentAllocationBottomSheet.css';

interface PaymentInfo {
  id: number;
  userId?: number;
  siteExecutorId?: number;
  userName?: string;
  amount: number;
  allocatedAmount?: number;
  remainingAmount?: number;
}

interface PaymentAllocationBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  payment: PaymentInfo | null;
  siteId: number;
  onSuccess?: () => void;
}

const PaymentAllocationBottomSheet: React.FC<PaymentAllocationBottomSheetProps> = ({
  isOpen,
  onClose,
  payment,
  siteId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [unpaidSessions, setUnpaidSessions] = useState<UnpaidSessionV2[]>([]);
  const [unpaidContracts, setUnpaidContracts] = useState<UnpaidContractV2[]>([]);
  const [unpaidEarnings, setUnpaidEarnings] = useState<UnpaidEarningForAllocationV2[]>([]);
  const [selectedActivities, setSelectedActivities] = useState<Map<number, number>>(new Map());
  const [selectedContracts, setSelectedContracts] = useState<Map<number, number>>(new Map());
  const [selectedEarnings, setSelectedEarnings] = useState<Map<number, number>>(new Map());
  const [allocationType, setAllocationType] = useState<string>('Оплата смен');
  const [error, setError] = useState<string>('');

  // Загрузка данных при открытии
  useEffect(() => {
    if (isOpen && payment) {
      loadAllocationData();
    } else {
      // Сброс состояния при закрытии
      resetState();
    }
  }, [isOpen, payment]);

  // Сброс выбранных элементов при смене типа распределения
  useEffect(() => {
    // Сбрасываем выбранные активности если не "Оплата смен"
    if (allocationType !== 'Оплата смен' && selectedActivities.size > 0) {
      setSelectedActivities(new Map());
    }
    // Сбрасываем выбранные контракты если не "Оплата контрактов"
    if (allocationType !== 'Оплата контрактов' && selectedContracts.size > 0) {
      setSelectedContracts(new Map());
    }
    if (allocationType !== 'Оплата начислений' && selectedEarnings.size > 0) {
      setSelectedEarnings(new Map());
    }
  }, [allocationType]);

  const resetState = () => {
    setSelectedActivities(new Map());
    setSelectedContracts(new Map());
    setSelectedEarnings(new Map());
    setUnpaidEarnings([]);
    setAllocationType('Оплата смен');
    setError('');
  };

  const loadAllocationData = async () => {
    if (!payment) return;

    setLoading(true);
    setError('');

    try {
      // Загружаем детали для распределения
      const details = await sitePaymentAllocationV2Api.getAllocationDetails(
        siteId,
        payment.id
      );

      setUnpaidSessions(details.unpaidSessions);
      setUnpaidContracts(details.unpaidContracts);

      if (payment.userId || payment.siteExecutorId) {
        const earnings = await sitePaymentAllocationV2Api.getUnpaidEarningsForAllocation(siteId, {
          userId: payment.userId,
          siteExecutorId: payment.siteExecutorId
        });
        setUnpaidEarnings(earnings);
      } else {
        setUnpaidEarnings([]);
      }
    } catch (err: any) {
      console.error('Error loading allocation data:', err);
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  // Расчет сумм с учётом текущего типа распределения
  const totalSelected = useCallback(() => {
    let total = 0;
    
    // Считаем активности только для типа "Оплата смен"
    if (allocationType === 'Оплата смен') {
      selectedActivities.forEach(amount => total += amount);
    }
    
    // Считаем контракты только для типа "Оплата контрактов"
    if (allocationType === 'Оплата контрактов') {
      selectedContracts.forEach(amount => total += amount);
    }

    if (allocationType === 'Оплата начислений') {
      selectedEarnings.forEach(amount => total += amount);
    }
    
    return total;
  }, [selectedActivities, selectedContracts, selectedEarnings, allocationType]);

  const remainingAmount = useCallback(() => {
    const remaining = (payment?.remainingAmount ?? payment?.amount ?? 0);
    return Math.max(0, remaining - totalSelected());
  }, [payment, totalSelected]);

  // Обработчики выбора активностей
  const handleActivityToggle = (activity: UnpaidActivityV2) => {
    const newSelected = new Map(selectedActivities);
    
    if (newSelected.has(activity.activityId)) {
      newSelected.delete(activity.activityId);
    } else {
      // Автоматически выбираем сумму, не превышающую остаток
      const maxAmount = Math.min(activity.remainingAmount, remainingAmount() + (selectedActivities.get(activity.activityId) || 0));
      newSelected.set(activity.activityId, maxAmount);
    }
    
    setSelectedActivities(newSelected);
  };

  const handleActivityAmountChange = (activityId: number, amount: number) => {
    // Находим активность во всех сессиях
    let activity: UnpaidActivityV2 | undefined;
    for (const session of unpaidSessions) {
      activity = session.activities.find(a => a.activityId === activityId);
      if (activity) break;
    }
    if (!activity) return;

    const maxAmount = Math.min(
      activity.remainingAmount,
      remainingAmount() + (selectedActivities.get(activityId) || 0)
    );
    
    const validAmount = Math.max(0, Math.min(amount, maxAmount));
    
    const newSelected = new Map(selectedActivities);
    if (validAmount > 0) {
      newSelected.set(activityId, validAmount);
    } else {
      newSelected.delete(activityId);
    }
    setSelectedActivities(newSelected);
  };

  // Обработчики выбора контрактов
  const handleContractToggle = (contract: UnpaidContractV2) => {
    const newSelected = new Map(selectedContracts);
    
    if (newSelected.has(contract.contractId)) {
      newSelected.delete(contract.contractId);
    } else {
      const maxAmount = Math.min(contract.remainingAmount, remainingAmount() + (selectedContracts.get(contract.contractId) || 0));
      newSelected.set(contract.contractId, maxAmount);
    }
    
    setSelectedContracts(newSelected);
  };

  const handleContractAmountChange = (contractId: number, amount: number) => {
    const contract = unpaidContracts.find(c => c.contractId === contractId);
    if (!contract) return;

    const maxAmount = Math.min(
      contract.remainingAmount,
      remainingAmount() + (selectedContracts.get(contractId) || 0)
    );
    
    const validAmount = Math.max(0, Math.min(amount, maxAmount));
    
    const newSelected = new Map(selectedContracts);
    if (validAmount > 0) {
      newSelected.set(contractId, validAmount);
    } else {
      newSelected.delete(contractId);
    }
    setSelectedContracts(newSelected);
  };

  const handleEarningToggle = (earning: UnpaidEarningForAllocationV2) => {
    const newSelected = new Map(selectedEarnings);

    if (newSelected.has(earning.id)) {
      newSelected.delete(earning.id);
    } else {
      const maxAmount = Math.min(
        earning.remaining,
        remainingAmount() + (selectedEarnings.get(earning.id) || 0)
      );
      newSelected.set(earning.id, maxAmount);
    }

    setSelectedEarnings(newSelected);
  };

  const handleEarningAmountChange = (earningId: number, amount: number) => {
    const earning = unpaidEarnings.find((e) => e.id === earningId);
    if (!earning) return;

    const maxAmount = Math.min(
      earning.remaining,
      remainingAmount() + (selectedEarnings.get(earningId) || 0)
    );

    const validAmount = Math.max(0, Math.min(amount, maxAmount));

    const newSelected = new Map(selectedEarnings);
    if (validAmount > 0) {
      newSelected.set(earningId, validAmount);
    } else {
      newSelected.delete(earningId);
    }
    setSelectedEarnings(newSelected);
  };

  // Автоматическое распределение
  const handleAutoAllocate = async () => {
    if (!payment) return;
    if (!payment.userId && !payment.siteExecutorId) {
      setError('Для автораспределения нужен исполнитель (участник или внешний)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await sitePaymentAllocationV2Api.autoAllocatePayment(
        siteId,
        payment.id,
        {
          userId: payment.userId,
          siteExecutorId: payment.siteExecutorId,
          amount: payment.remainingAmount ?? payment.amount
        }
      );

      if (response.success) {
        onSuccess?.();
        onClose();
      }
    } catch (err: any) {
      console.error('Error auto-allocating payment:', err);
      setError(err.response?.data || 'Ошибка автоматического распределения');
    } finally {
      setLoading(false);
    }
  };

  // Ручное распределение
  const handleManualAllocate = async () => {
    if (!payment) return;

    if (totalSelected() === 0) {
      setError('Выберите активности, контракты или начисления для оплаты');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const request: PaymentAllocationV2Request = {
        activityAllocations:
          allocationType === 'Оплата смен'
            ? Array.from(selectedActivities.entries()).map(([activityId, amount]) => ({
                activityId,
                amount
              }))
            : allocationType === 'Оплата начислений'
              ? Array.from(selectedEarnings.entries()).map(([earningId, amount]) => ({
                  activityId: 0,
                  earningId,
                  amount
                }))
              : [],
        contractAllocations: allocationType === 'Оплата контрактов' ? Array.from(selectedContracts.entries()).map(([contractId, amount]) => ({
          contractId,
          amount
        })) : []
      };

      const response = await sitePaymentAllocationV2Api.allocatePayment(
        siteId,
        payment.id,
        request
      );

      if (response.success) {
        onSuccess?.();
        onClose();
      }
    } catch (err: any) {
      console.error('Error allocating payment:', err);
      const conflictMsg = getPaymentAllocationConflictMessage(err);
      setError(conflictMsg || err.response?.data || 'Ошибка распределения платежа');
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0₽';
    return `${amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}₽`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (!payment) return null;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Распределение платежа"
      size="large"
    >
      <div className="payment-allocation-sheet">
        {/* Информация о платеже */}
        <div className="allocation-section">
          <div className="payment-info-card">
            <div className="payment-info-row">
              <span className="label">Сотрудник:</span>
              <span className="value">{payment.userName}</span>
            </div>
            <div className="payment-info-row">
              <span className="label">Сумма платежа:</span>
              <span className="value">{formatAmount(payment.amount)}</span>
            </div>
            {payment.allocatedAmount !== undefined && payment.allocatedAmount > 0 && (
              <div className="payment-info-row">
                <span className="label">Распределено:</span>
                <span className="value allocated">{formatAmount(payment.allocatedAmount)}</span>
              </div>
            )}
            <div className="payment-info-row highlight">
              <span className="label">Осталось распределить:</span>
              <span className="value">{formatAmount(remainingAmount())}</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="allocation-error">
            {error}
          </div>
        )}

        {/* Выбор типа распределения - САМЫЙ ПЕРВЫЙ */}
        <div className="allocation-section">
          <h3 className="section-title">Тип распределения</h3>
          <HorizontalCategoryTabs
            categories={['Оплата смен', 'Оплата контрактов', 'Оплата начислений']}
            value={allocationType}
            onChange={(value) => setAllocationType(value || 'Оплата смен')}
            showAllOption={false}
            size="medium"
          />
        </div>

        {/* Показываем активности при типе "Оплата смен" */}
        {allocationType === 'Оплата смен' && (
          <>
            {/* Кнопка автораспределения */}
            <div className="allocation-section">
              <StatusActionButton
                variant="primary"
                onClick={handleAutoAllocate}
                disabled={loading || unpaidSessions.length === 0}
                fullWidth
                icon="ri-magic-line"
              >
                Автоматически распределить
              </StatusActionButton>
              <p className="help-text">
                Автоматически распределит платеж по неоплаченным активностям (старые сначала)
              </p>
            </div>

            {/* Неоплаченные активности (отсортированные по датам) */}
        {unpaidSessions.length > 0 && (() => {
          // Собираем все активности из всех сессий в один массив
          const allActivities = unpaidSessions.flatMap(session => 
            session.activities.map(activity => ({
              ...activity,
              siteName: session.siteName
              // typeOfWorkName теперь берется из самой активности, а не из сессии
            }))
          );
          
          // Сортируем по дате (от старых к новым)
          const sortedActivities = allActivities.sort((a, b) => 
            new Date(a.timeStart).getTime() - new Date(b.timeStart).getTime()
          );
          
          return (
            <div className="allocation-section">
              <h3 className="section-title">
                Неоплаченные активности ({sortedActivities.length})
              </h3>
              <div className="activities-list">
                {sortedActivities.map(activity => (
                  <div
                    key={activity.activityId}
                    className={`allocation-item ${selectedActivities.has(activity.activityId) ? 'selected' : ''}`}
                    onClick={() => handleActivityToggle(activity)}
                  >
                    <div className="item-content">
                      <div className="item-title">
                        {formatDate(activity.timeStart)}
                        {activity.timeEnd && ` - ${formatDate(activity.timeEnd)}`}
                      </div>
                      <div className="item-subtitle">
                        {activity.siteName} • {activity.typeOfWorkName}
                      </div>
                      <div className="item-subtitle">
                        {activity.hours.toFixed(1)} ч × {activity.rate}₽/ч
                      </div>
                      <div className="item-amounts">
                        <span className="amount-label">К оплате:</span>
                        <span className="amount-value">{formatAmount(activity.remainingAmount)}</span>
                      </div>
                    </div>
                    {selectedActivities.has(activity.activityId) && (
                      <div className="item-amount-input" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="number"
                          value={selectedActivities.get(activity.activityId)}
                          onChange={(e) => handleActivityAmountChange(activity.activityId, parseFloat(e.target.value) || 0)}
                          max={Math.min(activity.remainingAmount, remainingAmount() + (selectedActivities.get(activity.activityId) || 0))}
                          min={0}
                          step={100}
                          disabled={loading}
                        />
                        <span className="currency">₽</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
          </>
        )}

        {/* Неоплаченные начисления (премии и др.) */}
        {allocationType === 'Оплата начислений' && (
          <>
            {unpaidEarnings.length > 0 ? (
              <div className="allocation-section">
                <h3 className="section-title">К оплате по начислениям ({unpaidEarnings.length})</h3>
                <p className="help-text">
                  Погашает долг по ранее созданным начислениям (например почасовая премия).
                </p>
                <div className="activities-list">
                  {unpaidEarnings.map((earning) => (
                    <div
                      key={earning.id}
                      className={`allocation-item ${selectedEarnings.has(earning.id) ? 'selected' : ''}`}
                      onClick={() => handleEarningToggle(earning)}
                    >
                      <div className="item-content">
                        <div className="item-title">
                          {earning.earningTypeName}
                          {earning.createdAt && (
                            <span className="item-subtitle" style={{ marginLeft: 8 }}>
                              {formatDate(earning.createdAt)}
                            </span>
                          )}
                        </div>
                        {earning.description && (
                          <div className="item-subtitle">{earning.description}</div>
                        )}
                        <div className="item-amounts">
                          <span className="amount-label">Остаток:</span>
                          <span className="amount-value">{formatAmount(earning.remaining)}</span>
                        </div>
                      </div>
                      {selectedEarnings.has(earning.id) && (
                        <div className="item-amount-input" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="number"
                            value={selectedEarnings.get(earning.id)}
                            onChange={(e) => handleEarningAmountChange(earning.id, parseFloat(e.target.value) || 0)}
                            max={Math.min(
                              earning.remaining,
                              remainingAmount() + (selectedEarnings.get(earning.id) || 0)
                            )}
                            min={0}
                            step={100}
                            disabled={loading}
                          />
                          <span className="currency">₽</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="allocation-section">
                <p className="help-text">Нет неоплаченных начислений для этого исполнителя.</p>
              </div>
            )}
          </>
        )}

        {/* Показываем контракты при типе "Оплата контрактов" */}
        {allocationType === 'Оплата контрактов' && (
          <>
            {/* Неоплаченные контракты */}
            {unpaidContracts.length > 0 && (
              <div className="allocation-section">
                <h3 className="section-title">
                  Неоплаченные контракты ({unpaidContracts.length})
                </h3>
                <div className="items-list">
                  {unpaidContracts.map(contract => (
                    <div
                      key={contract.contractId}
                      className={`allocation-item ${selectedContracts.has(contract.contractId) ? 'selected' : ''}`}
                      onClick={() => handleContractToggle(contract)}
                    >
                      <div className="item-content">
                        <div className="item-title">{contract.title}</div>
                        <div className="item-subtitle">
                          {contract.siteName} • {contract.typeOfWorkName}
                        </div>
                        <div className="item-amounts">
                          <span className="amount-label">К оплате:</span>
                          <span className="amount-value">{formatAmount(contract.remainingAmount)}</span>
                        </div>
                      </div>
                      {selectedContracts.has(contract.contractId) && (
                        <div className="item-amount-input" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="number"
                            value={selectedContracts.get(contract.contractId)}
                            onChange={(e) => handleContractAmountChange(contract.contractId, parseFloat(e.target.value) || 0)}
                            max={Math.min(contract.remainingAmount, remainingAmount() + (selectedContracts.get(contract.contractId) || 0))}
                            min={0}
                            step={100}
                            disabled={loading}
                          />
                          <span className="currency">₽</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Итоги */}
        <div className="allocation-summary">
          {allocationType === 'Оплата смен' && (
            <div className="summary-row">
              <span>Выбрано активностей:</span>
              <span>{selectedActivities.size}</span>
            </div>
          )}
          {allocationType === 'Оплата контрактов' && (
            <div className="summary-row">
              <span>Выбрано контрактов:</span>
              <span>{selectedContracts.size}</span>
            </div>
          )}
          {allocationType === 'Оплата начислений' && (
            <div className="summary-row">
              <span>Выбрано начислений:</span>
              <span>{selectedEarnings.size}</span>
            </div>
          )}
          <div className="summary-row total">
            <span>Итого к распределению:</span>
            <span>{formatAmount(totalSelected())}</span>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="allocation-actions">
          <StatusActionButton
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            fullWidth
          >
            Отмена
          </StatusActionButton>
          <StatusActionButton
            variant="primary"
            onClick={handleManualAllocate}
            disabled={loading || totalSelected() === 0}
            loading={loading}
            fullWidth
          >
            Распределить
          </StatusActionButton>
        </div>
      </div>
    </BottomSheet>
  );
};

export default PaymentAllocationBottomSheet;

